import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { type Routine, useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useState, type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { IconButton, Popover, XStack, YStack } from '@/components/ui';
import { Slider } from '@/components/ui/Slider';
import { Switch } from '@/components/ui/Switch';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { useRoutineRefresh } from '@/features/routines/use-routine-refresh';
import { ProactiveAgentList } from '@/features/settings/proactive-agent-list';
import { useVisibleRoutines } from '@/features/settings/proactive-agent.query';
import { openSettings } from '@/features/settings/settings-navigation';
import { pinnableRoutines } from '@/features/widgets/routine-report';
import { useIsInternalUser } from '@/hooks/useIsInternalUser';
import { cn } from '@/lib/utils';
import { settingsStore } from '@/stores/settings.store';
import { trackEvent } from '@/utils/analytics';

import { GmailArchiveSettings } from './components/gmail-archive-settings';
import { ImportanceThresholdSlider } from './components/ImportanceThresholdSlider';
import {
  homePreferencesStore,
  type HomeSectionId,
} from './home-preferences.store';
import { useMinCardImportance } from './use-min-card-importance';
import { useProactiveAgentFeed } from './use-proactive-agent-feed';

const SECTION_LABELS: Record<HomeSectionId, string> = {
  'upcoming-meetings': 'Schedule',
  'pinned-routines': 'Routines',
  'integration-suggestions': 'Suggested connections',
  feed: 'Feed',
};

const MIN_ROUTINES_FOR_SEARCH = 6;

const routinesMatchingQuery = (routines: Routine[], query: string) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return routines;
  return routines.filter((routine) =>
    routine.title.toLowerCase().includes(normalizedQuery)
  );
};

const sortRoutinesPinnedFirst = (
  routines: Routine[],
  pinnedRoutineIds: Set<number>
) =>
  [...routines].sort((left, right) => {
    const pinnedDelta =
      Number(pinnedRoutineIds.has(right.referenceId)) -
      Number(pinnedRoutineIds.has(left.referenceId));
    if (pinnedDelta !== 0) return pinnedDelta;
    return left.title.localeCompare(right.title);
  });

type HomeSettingsToggleRowProps = {
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  ariaLabel: string;
  privateLabel?: boolean;
  className?: string;
};

const HomeSettingsToggleRow = ({
  id,
  label,
  checked,
  onCheckedChange,
  ariaLabel,
  privateLabel = false,
  className,
}: HomeSettingsToggleRowProps) => (
  <XStack
    className={cn(
      'items-center gap-2 rounded-md px-2.5 py-2 hover:bg-surface-hover',
      className
    )}
  >
    <label
      htmlFor={id}
      className={cn(
        'min-w-0 flex-1 truncate text-sm text-foreground',
        privateLabel && 'no-record'
      )}
    >
      {label}
    </label>
    <Switch
      id={id}
      checked={checked}
      className="scale-90"
      onCheckedChange={onCheckedChange}
      aria-label={ariaLabel}
    />
  </XStack>
);

type SortableSectionRowProps = {
  sectionId: HomeSectionId;
  children: ReactNode;
};

const SortableSectionRow = ({
  sectionId,
  children,
}: SortableSectionRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sectionId });

  return (
    <XStack
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        'items-center rounded-md hover:bg-surface-hover',
        isDragging && 'relative z-10 bg-surface-hover shadow-sm'
      )}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${SECTION_LABELS[sectionId]}`}
        className={cn(
          'flex h-9 w-6 shrink-0 touch-none items-center justify-center rounded-md text-foreground-subtle hover:text-foreground-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-strong',
          isDragging ? 'cursor-grabbing' : 'cursor-grab'
        )}
      >
        <Icons.gripVertical className="size-3.5" strokeWidth={2} />
      </button>
      <div className="min-w-0 flex-1">{children}</div>
    </XStack>
  );
};

const SettingsViewHeader = ({
  label,
  onBack,
}: {
  label: string;
  onBack: () => void;
}) => (
  <XStack className="items-center gap-2 px-2 py-1">
    <IconButton
      icon={Icons.chevronLeft}
      variant="ghost"
      size="xs"
      aria-label="Back to home sections"
      onClick={onBack}
    />
    <span className="text-xs font-medium text-foreground-muted">{label}</span>
  </XStack>
);

type HomeSettingsRoutinesViewProps = {
  routines: Routine[];
  pinnedRoutineIds: Set<number>;
  onBack: () => void;
};

const HomeSettingsRoutinesView = ({
  routines,
  pinnedRoutineIds,
  onBack,
}: HomeSettingsRoutinesViewProps) => {
  const [query, setQuery] = useState('');
  const showSearch = routines.length >= MIN_ROUTINES_FOR_SEARCH;
  const visibleRoutines = sortRoutinesPinnedFirst(
    routinesMatchingQuery(routines, query),
    pinnedRoutineIds
  );

  return (
    <YStack className="gap-1">
      <SettingsViewHeader label="Routines" onBack={onBack} />
      {showSearch && (
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search routines"
          aria-label="Search routines"
          autoFocus
          className="h-8 w-full rounded-md px-2.5 text-sm text-foreground placeholder:text-foreground-muted focus:bg-surface-hover focus:outline-none"
        />
      )}
      <YStack className="max-h-80 overflow-y-auto">
        {routines.length === 0 ? (
          <span className="px-2.5 py-3 text-sm text-foreground-muted">
            No active routines to show.
          </span>
        ) : visibleRoutines.length === 0 ? (
          <span className="px-2.5 py-3 text-sm text-foreground-muted">
            No matching routines.
          </span>
        ) : (
          visibleRoutines.map((routine) => {
            const toggleId = `home-routine-${routine.referenceId}`;
            const checked = pinnedRoutineIds.has(routine.referenceId);
            return (
              <HomeSettingsToggleRow
                key={routine.referenceId}
                id={toggleId}
                label={routine.title}
                checked={checked}
                privateLabel
                onCheckedChange={(nextChecked) => {
                  if (nextChecked) {
                    homePreferencesStore.addPinnedRoutine(routine.referenceId);
                  } else {
                    homePreferencesStore.removeInstance(
                      `pinned-routine:${routine.referenceId}`
                    );
                  }
                  trackEvent('HOME_PINNED_ROUTINE_TOGGLED', {
                    pinned: nextChecked,
                  });
                }}
                ariaLabel={`${checked ? 'Hide' : 'Show'} ${routine.title}`}
              />
            );
          })
        )}
      </YStack>
    </YStack>
  );
};

const AgentListView = ({ onBack }: { onBack: () => void }) => (
  <YStack className="gap-1">
    <SettingsViewHeader label="My agents" onBack={onBack} />
    <YStack className="max-h-96 overflow-y-auto px-2.5 py-1">
      <ProactiveAgentList className="gap-4" />
    </YStack>
  </YStack>
);

/** A row naming one of the agent states, which opens the list of agents */
const AgentListRow = ({
  label,
  value,
  onClick,
}: {
  label: string;
  value: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left hover:bg-surface-hover"
  >
    <span className="shrink-0 text-sm text-foreground">{label}</span>
    <span
      className="min-w-0 flex-1 truncate text-right text-xs text-foreground-subtle"
      title={value}
    >
      {value}
    </span>
    <Icons.chevronRight
      className="size-3.5 shrink-0 text-foreground-subtle"
      strokeWidth={2}
    />
  </button>
);

const ProactiveAgentSettings = observer(
  ({ onOpenAgents }: { onOpenAgents: () => void }) => {
    const feed = useProactiveAgentFeed();
    const { minImportance } = useMinCardImportance();

    return (
      <YStack className="gap-1 border-t border-border pt-1">
        <XStack className="items-center justify-between px-2 py-1">
          <span className="text-xs font-medium text-foreground-muted">
            Proactive agents
          </span>
          <button
            type="button"
            onClick={() => void openSettings('ProactiveAgent')}
            className="text-xs text-foreground-muted transition-colors hover:text-foreground"
          >
            Manage
          </button>
        </XStack>
        <HomeSettingsToggleRow
          id="home-proactive-agent-view"
          label="Proactive agent view"
          checked={feed.enabled}
          onCheckedChange={feed.setEnabled}
          ariaLabel="Internal proactive agent view"
        />
        {feed.enabled && feed.hasAgents && (
          <>
            <AgentListRow
              label="My agents"
              value={feed.selectedLabel}
              onClick={onOpenAgents}
            />
            {feed.selectedAgent !== 'none' && (
              <HomeSettingsToggleRow
                id="home-proactive-agent-hide-own-cards"
                label="Hide your own cards"
                checked={feed.hidesCardsOfNoAgent}
                onCheckedChange={feed.setHidesCardsOfNoAgent}
                ariaLabel="Hide your own cards"
              />
            )}
            <XStack className="items-center gap-3 rounded-md px-2.5 py-2">
              <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                Min importance
              </span>
              <ImportanceThresholdSlider className="w-24" />
              <span className="w-4 text-right text-xs tabular-nums text-foreground-muted">
                {minImportance}
              </span>
            </XStack>
          </>
        )}
      </YStack>
    );
  }
);

const FeedChatsSettings = observer(() => (
  <YStack className="gap-1 border-t border-border pt-1">
    <XStack className="items-center justify-between px-2 py-1">
      <span className="text-xs font-medium text-foreground-muted">Chats</span>
    </XStack>
    <HomeSettingsToggleRow
      id="home-feed-composer-chats"
      label="Show chats in feed"
      checked={settingsStore.feedComposerChats}
      onCheckedChange={(checked) => settingsStore.setFeedComposerChats(checked)}
      ariaLabel="Show chats in feed"
    />
    <XStack className="items-center gap-3 rounded-md px-2.5 py-2">
      <span className="min-w-0 flex-1 truncate text-sm text-foreground">
        Keep in Working
      </span>
      <Slider
        aria-label="Keep in Working"
        className="w-24"
        value={[settingsStore.feedChatWorkingHoldMinutes]}
        min={0}
        max={60}
        step={5}
        onValueChange={([value]) =>
          settingsStore.setFeedChatWorkingHoldMinutes(value)
        }
      />
      <span className="w-8 text-right text-xs tabular-nums text-foreground-muted">
        {settingsStore.feedChatWorkingHoldMinutes}m
      </span>
    </XStack>
  </YStack>
));

const HomeSettingsContent = observer(() => {
  const [view, setView] = useState<'sections' | 'routines' | 'agents'>(
    'sections'
  );
  const hasFeature = useFeature();
  const isInternalUser = useIsInternalUser();
  const { routines: routineStore } = useStores();
  useRoutineRefresh();
  const routines = pinnableRoutines(
    useVisibleRoutines(routineStore.userRoutines)
  );
  const pinnedRoutineIds = new Set(
    homePreferencesStore.entries.flatMap((entry) =>
      entry.kind === 'pinned-routine' ? [entry.routineId] : []
    )
  );

  const pinnedListedCount = routines.filter((routine) =>
    pinnedRoutineIds.has(routine.referenceId)
  ).length;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const scheduleEntry = homePreferencesStore.entries.find(
    (entry) => entry.kind === 'singleton' && entry.type === 'upcoming-meetings'
  );

  const isSectionListed = (sectionId: HomeSectionId) => {
    switch (sectionId) {
      case 'upcoming-meetings':
        return scheduleEntry !== undefined;
      case 'feed':
        return hasFeature(FEATURES.ACTION_CARDS_FEED);
      case 'integration-suggestions':
        return hasFeature(FEATURES.HOME_INTEGRATION_SUGGESTIONS);
      case 'pinned-routines':
        return true;
    }
  };
  const listedSections =
    homePreferencesStore.sectionOrder.filter(isSectionListed);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const sectionId = listedSections.find((id) => id === active.id);
    const targetId = listedSections.find((id) => id === over.id);
    if (!sectionId || !targetId) return;
    homePreferencesStore.moveSection(sectionId, targetId);
    trackEvent('HOME_SECTION_REORDERED', {
      section_id: sectionId,
      position: homePreferencesStore.sectionOrder.indexOf(sectionId),
    });
  };

  const renderSectionControl = (sectionId: HomeSectionId) => {
    const label = SECTION_LABELS[sectionId];
    const toggleId = `home-section-${sectionId}`;
    const visible = homePreferencesStore.isSectionVisible(sectionId);
    const toggle = (
      <Switch
        id={toggleId}
        checked={visible}
        className="scale-90"
        onCheckedChange={(next) => {
          homePreferencesStore.setSectionVisible(sectionId, next);
          trackEvent('HOME_SECTION_VISIBILITY_TOGGLED', {
            section_id: sectionId,
            visible: next,
          });
        }}
        aria-label={`Show ${label}`}
      />
    );

    if (sectionId === 'pinned-routines') {
      return (
        <XStack className="items-center gap-2 py-2 pl-1 pr-2.5">
          <button
            type="button"
            onClick={() => setView('routines')}
            aria-label={`${label} settings`}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
          >
            <span className="min-w-0 flex-1 truncate text-sm text-foreground">
              {label}
            </span>
            {routines.length > 0 && (
              <span className="shrink-0 text-xs text-foreground-subtle">
                {pinnedListedCount} of {routines.length}
              </span>
            )}
            <Icons.chevronRight
              className="size-3.5 shrink-0 text-foreground-subtle"
              strokeWidth={2}
            />
          </button>
          {toggle}
        </XStack>
      );
    }

    return (
      <XStack className="items-center gap-2 py-2 pl-1 pr-2.5">
        <label
          htmlFor={toggleId}
          className="min-w-0 flex-1 truncate text-sm text-foreground"
        >
          {label}
        </label>
        {toggle}
      </XStack>
    );
  };

  if (view === 'agents') {
    return <AgentListView onBack={() => setView('sections')} />;
  }

  if (view === 'routines') {
    return (
      <HomeSettingsRoutinesView
        routines={routines}
        pinnedRoutineIds={pinnedRoutineIds}
        onBack={() => setView('sections')}
      />
    );
  }

  return (
    <YStack className="gap-1">
      <XStack className="items-center justify-between px-2 py-1">
        <span className="text-xs font-medium text-foreground-muted">
          Home sections
        </span>
        <button
          type="button"
          onClick={() => {
            homePreferencesStore.reset();
            trackEvent('HOME_SETTINGS_RESET');
          }}
          className="text-xs text-foreground-muted transition-colors hover:text-foreground"
        >
          Reset
        </button>
      </XStack>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={listedSections}
          strategy={verticalListSortingStrategy}
        >
          <YStack>
            {listedSections.map((sectionId) => (
              <SortableSectionRow key={sectionId} sectionId={sectionId}>
                {renderSectionControl(sectionId)}
              </SortableSectionRow>
            ))}
          </YStack>
        </SortableContext>
      </DndContext>
      {hasFeature(FEATURES.ACTION_CARDS_FEED) && <FeedChatsSettings />}
      {hasFeature(FEATURES.GMAIL_FEED_AUTO_ARCHIVE) && <GmailArchiveSettings />}
      {isInternalUser && hasFeature(FEATURES.ACTION_CARDS_FEED) && (
        <ProactiveAgentSettings onOpenAgents={() => setView('agents')} />
      )}
    </YStack>
  );
});

export const HomeSettings = () => {
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) trackEvent('HOME_SETTINGS_OPENED');
      }}
    >
      <Popover.Trigger asChild>
        <IconButton
          icon={Icons.cog}
          variant="ghost"
          size="sm"
          aria-label="Configure home"
        />
      </Popover.Trigger>
      <Popover.Content align="end" className="w-80 p-2">
        {open && <HomeSettingsContent />}
      </Popover.Content>
    </Popover.Root>
  );
};
